import { Star, CheckCircle, XCircle, CreditCard, Clock, Download, Mail, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { projectTypeLabels } from "./labels";

export const ReviewsTab = (p) => (
  <div className="space-y-4" data-testid="reviews-panel">
    <div className="flex gap-2 mb-4">
      {[
        { value: "pending", label: "En attente" },
        { value: "approved", label: "Approuvés" },
        { value: "rejected", label: "Rejetés" },
        { value: "", label: "Tous" }
      ].map((f) => (
        <Button key={f.value} variant={p.reviewFilter === f.value ? "default" : "outline"} size="sm"
          onClick={() => p.setReviewFilter(f.value)}
          className={p.reviewFilter === f.value ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}
          data-testid={`review-filter-${f.value || "all"}`}>
          {f.label}
        </Button>
      ))}
    </div>
    {p.reviews.length > 0 && (
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3" data-testid="reviews-bulk-bar">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={p.selectedReviews.length === p.reviews.length}
            onChange={p.toggleSelectAllReviews} className="w-4 h-4 accent-orange-500" data-testid="select-all-reviews-checkbox" />
          Tout sélectionner
        </label>
        {p.selectedReviews.length > 0 && (
          <Button size="sm" variant="outline" onClick={p.handleBulkDeleteReviews}
            className="text-red-600 border-red-200 hover:bg-red-50 ml-auto" data-testid="bulk-delete-reviews-button">
            <Trash2 className="w-4 h-4 mr-1" />Supprimer la sélection ({p.selectedReviews.length})
          </Button>
        )}
      </div>
    )}
    {p.reviews.length === 0 ? (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Aucun avis {p.reviewFilter === "pending" ? "en attente" : ""}</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {p.reviews.map((review) => (
          <div key={review.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`review-${review.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={p.selectedReviews.includes(review.id)}
                  onChange={() => p.toggleReviewSelect(review.id)} className="w-4 h-4 accent-orange-500" data-testid={`select-review-${review.id}`} />
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
                  ))}
                </div>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                review.status === "approved" ? "bg-green-100 text-green-700" :
                review.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                {review.status === "approved" ? "Approuvé" : review.status === "rejected" ? "Rejeté" : "En attente"}
              </span>
            </div>
            <p className="text-slate-600 text-sm mb-3">"{review.comment}"</p>
            <div className="flex items-center justify-between text-sm">
              <div>
                <p className="font-medium text-slate-900">{review.reviewer_name}</p>
                <p className="text-slate-500">
                  {review.contractor_name && `Pour: ${review.contractor_name}`}
                  {review.project_type && ` — ${projectTypeLabels[review.project_type] || review.project_type}`}
                </p>
              </div>
              <p className="text-slate-400 text-xs">{new Date(review.created_at).toLocaleDateString("fr-BE")}</p>
            </div>
            {review.status === "pending" && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button size="sm" onClick={() => p.handleModerate(review.id, "approved")} disabled={p.moderatingId === review.id}
                  className="bg-green-600 hover:bg-green-700 text-white flex-1" data-testid={`approve-review-${review.id}`}>
                  <CheckCircle className="w-4 h-4 mr-1" />Approuver
                </Button>
                <Button size="sm" variant="outline" onClick={() => p.handleModerate(review.id, "rejected")} disabled={p.moderatingId === review.id}
                  className="text-red-600 border-red-200 hover:bg-red-50 flex-1" data-testid={`reject-review-${review.id}`}>
                  <XCircle className="w-4 h-4 mr-1" />Rejeter
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

export const PaymentsTab = (p) => (
  <div data-testid="payments-panel">
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="payments-export-bar">
      <span className="text-sm font-medium text-slate-700">Export comptabilité :</span>
      <Input type="month" value={p.pdfMonth} onChange={(e) => p.setPdfMonth(e.target.value)} className="w-44" data-testid="pdf-month-input" />
      <Button variant="outline" size="sm" onClick={p.handleExportPaymentsPdf} data-testid="export-pdf-button">
        <Download className="w-4 h-4 mr-2" />Export PDF
      </Button>
      <Button variant="outline" size="sm" onClick={p.handleSendMonthlyReport} data-testid="send-monthly-report-button">
        <Mail className="w-4 h-4 mr-2" />Recevoir par email
      </Button>
      <span className="text-xs text-slate-500 basis-full sm:basis-auto sm:ml-auto">Le rapport du mois écoulé est aussi envoyé automatiquement chaque 1er du mois</span>
    </div>
    {p.payments.length === 0 ? (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 font-medium">Aucun paiement enregistre</p>
        <p className="text-sm text-slate-400 mt-1">Les paiements Stripe apparaitront ici</p>
      </div>
    ) : (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="payments-select-bar">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
            <Checkbox checked={p.payments.length > 0 && p.selectedPayments.length === p.payments.length} onCheckedChange={p.togglePaymentSelectAll} data-testid="select-all-payments" />
            Tout sélectionner
          </label>
          {p.selectedPayments.length > 0 && (
            <>
              <span className="text-sm font-semibold text-slate-900">{p.selectedPayments.length} paiement(s) sélectionné(s)</span>
              <Button size="sm" variant="outline" onClick={p.handleBulkDeletePayments} className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="bulk-delete-payments-button">
                <Trash2 className="w-4 h-4 mr-1" />Supprimer la sélection
              </Button>
              <button onClick={() => p.setSelectedPayments([])} className="text-sm text-slate-500 hover:text-slate-700 ml-auto" data-testid="payments-clear-selection">Tout désélectionner</button>
            </>
          )}
        </div>
        {p.payments.map((tx) => (
          <div key={tx.id} className={`bg-white rounded-xl border p-6 ${p.selectedPayments.includes(tx.id) ? "border-orange-300 bg-orange-50/40" : "border-slate-200"}`} data-testid={`payment-${tx.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="pt-1"><Checkbox checked={p.selectedPayments.includes(tx.id)} onCheckedChange={() => p.togglePaymentSelection(tx.id)} data-testid={`select-payment-${tx.id}`} /></div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1 ${
                    tx.payment_status === "confirmed" ? "bg-green-100 text-green-700" :
                    tx.payment_status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                    {tx.payment_status === "confirmed" ? (<><CheckCircle className="w-3 h-3" /> Confirme</>) :
                     tx.payment_status === "pending" ? (<><Clock className="w-3 h-3" /> En attente</>) :
                     (<><XCircle className="w-3 h-3" /> {tx.payment_status}</>)}
                  </span>
                  {tx.payment_reference && (<span className="text-sm font-mono text-orange-600 font-bold">{tx.payment_reference}</span>)}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-slate-500">Entrepreneur</p>
                    <p className="font-medium text-slate-900 text-sm">{tx.contractor_name}</p>
                    <p className="text-xs text-slate-400">{tx.contractor_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Lead</p>
                    <p className="font-medium text-slate-900 text-sm">{tx.lead_type}</p>
                    <p className="text-xs text-slate-400">{tx.lead_city} - {tx.lead_contact}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Montant</p>
                    <p className="font-bold text-xl text-orange-600">{tx.amount} €</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Date</p>
                    <p className="text-sm text-slate-700">{new Date(tx.created_at).toLocaleDateString("fr-BE")} {new Date(tx.created_at).toLocaleTimeString("fr-BE", {hour:"2-digit", minute:"2-digit"})}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);
